import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { PasswordValidator } from '../../utils/password-validator';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService, DialogService, LanguageService } from 'src/app/services';
import * as _ from 'lodash';
import { CognitoUser, AuthenticationDetails, CognitoUserSession } from 'amazon-cognito-identity-js';
import { LoginService } from '../login/login.service';
import { environment } from 'src/environments/environment';
import { HTTP_HEADER_AUTHORIZATION, HTTP_HEADER_CONTENT_TYPE, HTTP_HEADER_ESC_TIMEZONE, HTTP_HEADER_ESC_USER_DETAIL, MIME_TYPE_JSON, Path } from 'src/app/utils/constant';

@Component({
  selector: 'esc-change-expired-password',
  templateUrl: './change-expired-password.component.html',
  styleUrls: ['./change-expired-password.component.scss']
})
export class ChangeExpiredPasswordComponent implements OnInit {
  public show: any = {
    old: false,
    confirm: false,
    password: false
  };
  public updatePasswordForm: FormGroup;
  public isLoading = false;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<ChangeExpiredPasswordComponent>,
    private authService: AuthService,
    private dialogService: DialogService,
    private languageService: LanguageService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { username: string, oldPassword: string, session: CognitoUserSession }
  ) {}

  ngOnInit() {
    this.updatePasswordForm = this.fb.group({
      oldPassword: [_.get(this.data, 'oldPassword')],
      password: [null,
        Validators.compose([
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(60),
          Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\^$*.\[\]{}()?\-\"!@#%&/\\,><':;|_~`])[A-Za-z\d\^$*.\[\]{}()?\-\"!@#%&/\\,><':;|_~`]{0,}/)
        ])
      ],
      confirmPassword: [null, Validators.required],
    }, {
      validator: PasswordValidator.matchPassword
    });

    this.setupPasswordValidation();
  }

  private setupPasswordValidation() {
    this.updatePasswordForm.get('password').valueChanges.subscribe(() => {
      this.updatePasswordForm.get('confirmPassword').updateValueAndValidity({ emitEvent: false });
    });
    this.updatePasswordForm.get('confirmPassword').valueChanges.subscribe(() => {
      this.updatePasswordForm.get('password').updateValueAndValidity({ emitEvent: false });
    });
  }

  onSubmit() {
    if (this.updatePasswordForm.invalid) return;

    const newPassword = this.updatePasswordForm.get('password').value;
    // const cognitoUser = this.authService.userPool.getCurrentUser();

    this.isLoading = true;

    const onSuccess = async () => {
      try {
        sessionStorage.setItem("sessionData", "loggedIn");

        // http service is not working on this implementation for unknown reasons
        // will use fetch api for now until the cause of the issue is found
        const headers = {
          [HTTP_HEADER_CONTENT_TYPE]: MIME_TYPE_JSON,
          [HTTP_HEADER_AUTHORIZATION]: _.get(this.data, 'session').getIdToken().getJwtToken(),
          [HTTP_HEADER_ESC_USER_DETAIL]: localStorage.getItem("userDetails"),
          [HTTP_HEADER_ESC_TIMEZONE]: localStorage.getItem("escTimezone")
        };

        fetch(environment.api.endpoints[0].endpoint + Path.LOGIN.UPDATE_LAST_CHANGE_PASSWORD_DATE, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({})
        })
        .then(response => {
          this.authService.logout();
          this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_RELOGIN_NEW_PASSWORD", {}))
        })
        .catch(err => {
          this.dialogService.openSnackBar(_.get(err, 'message'));
          // this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_ERROR_UPDATING", {}));
        })

        await this.authService.initAWSCredentials();

      } catch (err) {
        // this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_ERROR_UPDATING", {}));
        this.authService.logout();
        if (environment.showLogs) {
          console.error(err);
        }

        this.isLoading = false;
      } finally {
        // Close dialog with success
        this.dialogRef.close({
          oldPassword: this.updatePasswordForm.get('oldPassword')?.value,
          newPassword
        });

        this.isLoading = false;
      }
    };

    this.reauthenticateAndChangePassword(_.get(this.data, 'username'), this.updatePasswordForm.get('oldPassword')?.value, newPassword, onSuccess);
  }

  private reauthenticateAndChangePassword(username: string, oldPassword: string, newPassword: string, onSuccess: Function) {
    if (!username) {
      this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_ERROR_UPDATING", {}));
      this.authService.logout();
      this.isLoading = false;
      return;
    }

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: oldPassword
    });

    const userData = {
      Username: username,
      Pool: this.authService.userPool
    };

    const user = new CognitoUser(userData);

    user.authenticateUser(authDetails, {
      onSuccess: async (session) => {
        this.changePasswordWithSession(user, oldPassword, newPassword, onSuccess);
      },
      onFailure: (err) => {
        this.dialogService.openSnackBar(_.get(err, 'message'));
        // this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_ERROR_UPDATING", {}));
        this.authService.logout();
        this.isLoading = false;
      }
    });
  }

  private changePasswordWithSession(user: CognitoUser, oldPassword: string, newPassword: string, onSuccess: Function) {
    user.changePassword(oldPassword, newPassword, (err, result) => {
      if (err) {
        const errorMessage = _.get(err, 'message', '');
        if (errorMessage.toLowerCase().includes('has previously been used')) {
          // this.updatePasswordForm.get('confirmPassword')?.setErrors({ invalidPassword: true });
          this.updatePasswordForm.get('password')?.setErrors({ reusedPassword: true });
        } else if (errorMessage.toLowerCase().includes('exceeded')) {
          this.dialogService.openSnackBar('Too many password change attempts. Please try again in 1 hour.');
        } else {
          this.updatePasswordForm.get('oldPassword')?.setErrors({ invalidPassword: true });
          this.updatePasswordForm.get('confirmPassword')?.setErrors({ invalidPassword: true });
          this.updatePasswordForm.get('password')?.setErrors({ invalidPassword: true });
          this.dialogService.openSnackBar(errorMessage);
        }
        // this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_ERROR_UPDATING", {}));
        this.authService.logout();
        this.isLoading = false;
        return;
      }
      onSuccess();
    });
  }
}
